// Visual language follows the other system pages (Departments, Leave
// Management, Employee Actions): a glass header card tucked under the topbar,
// shared stat cards, glass section cards with an icon title, underline tabs,
// and the same #2e7cba accent. Inputs use unifiedControlStyle so they line up
// with ReusableDropdown / DatePicker / SearchBar.
import { unifiedControlStyle } from "../../components/ReusableDropdown";

export const BRAND = "#2e7cba";
export const BRAND_DARK = "#256a9f";
export const DANGER = "#ef4444";
export const SUCCESS = "#10b981";

export const getBroadcastStyles = (screenSize, theme, language) => {
  const { isMobile, isTablet } = screenSize;
  const isRTL = language === "ar";
  const dark = theme === "dark";

  const c = {
    text: dark ? "#ffffff" : "#0f172a",
    body: dark ? "rgba(255, 255, 255, 0.85)" : "#334155",
    muted: dark ? "rgba(255, 255, 255, 0.6)" : "#64748b",
    border: dark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
    rowBorder: dark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9",
    softBg: dark ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
    chipBg: dark ? "rgba(46, 124, 186, 0.2)" : "rgba(46, 124, 186, 0.08)",
    chipText: dark ? "#8cc4ee" : BRAND,
    dangerSoft: dark ? "rgba(239, 68, 68, 0.12)" : "#fff5f5",
    track: dark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
  };

  // Same field look as the Add Employee modal (translucent fill, 1.5px border).
  const control = {
    ...unifiedControlStyle(theme, isRTL),
    padding: "12px 14px",
    borderRadius: "10px",
    border: dark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(0,0,0,0.08)",
    background: dark ? "rgba(15, 23, 42, 0.4)" : "rgba(248, 250, 252, 0.9)",
    color: dark ? "#e6eef8" : "#0f172a",
    transition: "all 300ms ease",
  };

  // Same glass surface as Mo3tiStatCard / the Departments cards.
  const glass = {
    background: dark ? "rgba(255, 255, 255, 0.02)" : "rgba(255, 255, 255, 0.9)",
    border: dark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.05)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: dark
      ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  };

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: isMobile ? "11px 16px" : "11px 18px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 700,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    border: "1px solid transparent",
    transition: "all 0.2s ease",
    outline: "none",
  };
  const btn = (bg, color, border = "transparent") => (disabled = false) => ({
    ...btnBase,
    background: bg,
    color,
    borderColor: border,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  });

  return {
    colors: c,

    container: {
      padding: isMobile ? "0 12px" : "0",
      direction: isRTL ? "rtl" : "ltr",
      color: c.text,
    },

    // ── header card ──
    header: {
      ...glass,
      borderRadius: isMobile ? "16px" : "24px",
      padding: isMobile ? "20px" : "32px",
      marginTop: "-36px",
      marginBottom: "22px",
      display: "flex",
      alignItems: isMobile ? "stretch" : "flex-start",
      justifyContent: "space-between",
      flexDirection: isMobile ? "column" : "row",
      flexWrap: "wrap",
      gap: isMobile ? "18px" : "24px",
      animation: "bcSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    headerText: { minWidth: 0, flex: 1 },
    title: {
      fontSize: isMobile ? "22px" : "28px",
      fontWeight: 700,
      letterSpacing: isRTL ? 0 : "-0.5px",
      color: dark ? "#f1f5f9" : "#1e293b",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    subtitle: {
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: 500,
      color: dark ? "#cbd5e1" : "#64748b",
      margin: "12px 0 0 0",
      lineHeight: 1.6,
    },

    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
      gap: "clamp(16px, 3vw, 24px)",
      marginBottom: "24px",
    },

    // ── tabs (same underline tabs as Advancements / Document Requests) ──
    tabsCard: {
      ...glass,
      borderRadius: "16px",
      padding: isMobile ? "4px 12px 0" : "6px 20px 0",
      marginBottom: "24px",
    },
    tabContainer: {
      display: "flex",
      gap: "4px",
      overflowX: "auto",
    },
    tab: (active) => ({
      padding: isMobile ? "12px 14px" : "14px 20px",
      border: "none",
      background: "transparent",
      color: active ? (dark ? "#60a5fa" : BRAND) : c.muted,
      fontSize: "14px",
      fontWeight: active ? 700 : 600,
      fontFamily: "inherit",
      cursor: "pointer",
      borderBottom: `3px solid ${active ? BRAND : "transparent"}`,
      transition: "color 0.2s ease, border-color 0.2s ease",
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    }),

    // ── section cards ──
    sectionCard: {
      ...glass,
      borderRadius: "16px",
      padding: isMobile ? "18px" : "24px",
      marginBottom: "24px",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
      marginBottom: isMobile ? "16px" : "20px",
      paddingBottom: isMobile ? "12px" : "16px",
      borderBottom: `1px solid ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
    },
    sectionTitle: {
      fontSize: isMobile ? "16px" : "18px",
      fontWeight: 700,
      color: c.text,
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    stepNumber: {
      width: "26px",
      height: "26px",
      borderRadius: "8px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontSize: "13px",
      fontWeight: 700,
      background: c.chipBg,
      color: c.chipText,
    },
    sectionSubtitle: { fontSize: "13.5px", color: c.muted, margin: "4px 0 0 0", lineHeight: 1.5 },
    sectionActions: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },

    // ── form ──
    grid2: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
      gap: isMobile ? "0" : "0 16px",
    },
    field: { marginBottom: "16px", minWidth: 0 },
    // Field label — Add Employee modal's formLabel.
    label: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "12px",
      fontWeight: 700,
      marginBottom: "10px",
      color: dark ? "#94a3b8" : "#475569",
      textTransform: isRTL ? "none" : "uppercase",
      letterSpacing: isRTL ? 0 : "0.6px",
    },
    hint: { fontSize: "12.5px", color: c.muted, lineHeight: 1.6, margin: "6px 0 0 0" },
    input: control,
    textarea: { ...control, minHeight: "104px", resize: "vertical", lineHeight: 1.6 },
    subLabel: {
      fontSize: "12.5px",
      fontWeight: 700,
      color: c.muted,
      margin: "18px 0 10px",
      textTransform: isRTL ? "none" : "uppercase",
      letterSpacing: isRTL ? 0 : "0.04em",
    },
    // Group heading inside a modal — same as the Add Employee modal's SectionTitle.
    groupTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      fontSize: "13px",
      fontWeight: 800,
      color: dark ? "#60a5fa" : BRAND,
      borderInlineStart: `3px solid ${dark ? "rgba(96, 165, 250, 0.6)" : BRAND}`,
      paddingInlineStart: "12px",
      margin: "28px 0 16px",
      textTransform: isRTL ? "none" : "uppercase",
      letterSpacing: isRTL ? 0 : "0.6px",
      minHeight: "24px",
    },
    groupTitleText: { display: "inline-flex", alignItems: "center", gap: "8px" },
    row: { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" },
    errorList: {
      margin: "4px 0 16px",
      padding: "12px 14px",
      paddingInlineStart: "30px",
      borderRadius: "10px",
      background: c.dangerSoft,
      border: "1px solid rgba(239, 68, 68, 0.25)",
      color: dark ? "#fca5a5" : "#b91c1c",
      fontSize: "13px",
      lineHeight: 1.7,
    },

    // Segmented control (Now / Schedule, Build rules / Saved audience…)
    segmented: {
      display: "flex",
      gap: "3px",
      padding: "3px",
      borderRadius: "12px",
      background: dark ? "rgba(15, 23, 42, 0.6)" : "#f1f5f9",
      border: `1px solid ${c.border}`,
    },
    segment: (active) => ({
      flex: 1,
      border: "none",
      borderRadius: "9px",
      padding: "9px 12px",
      fontSize: "13.5px",
      fontWeight: 700,
      fontFamily: "inherit",
      cursor: "pointer",
      whiteSpace: "nowrap",
      background: active ? (dark ? "rgba(46, 124, 186, 0.35)" : "#ffffff") : "transparent",
      color: active ? (dark ? "#ffffff" : BRAND) : c.muted,
      boxShadow: active && !dark ? "0 1px 4px rgba(15, 23, 42, 0.1)" : "none",
      transition: "all 0.15s ease",
    }),

    // Template picker chips
    chipGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(min(180px, 100%), 1fr))",
      gap: "10px",
    },
    pickChip: (active) => ({
      border: `1.5px solid ${active ? BRAND : c.border}`,
      background: active ? c.chipBg : dark ? "rgba(255, 255, 255, 0.02)" : "#ffffff",
      color: c.text,
      borderRadius: "12px",
      padding: "12px 14px",
      cursor: "pointer",
      textAlign: "start",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: 0,
      fontFamily: "inherit",
      transition: "border-color 0.15s ease, background 0.15s ease",
    }),
    pickChipName: { fontSize: "13.5px", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    pickChipMeta: { fontSize: "12px", color: c.muted },

    // ── buttons ──
    btnPrimary: btn(BRAND, "#ffffff"),
    btnSuccess: btn(SUCCESS, "#ffffff"),
    btnDanger: btn(DANGER, "#ffffff"),
    btnSecondary: btn(dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.05)", c.body, c.border),
    btnSmall: { padding: "7px 12px", fontSize: "13px", borderRadius: "10px" },
    iconBtn: (tone = "default") => ({
      width: "34px",
      height: "34px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      flexShrink: 0,
      transition: "background 0.2s ease, color 0.2s ease",
      background: "transparent",
      color: tone === "danger" ? DANGER : c.muted,
    }),
    // Filled pill buttons used in table rows (same as Departments / Payroll).
    actionBtn: (variant = "neutral") => ({
      padding: "8px 12px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      fontSize: "12px",
      fontWeight: 600,
      fontFamily: "inherit",
      transition: "all 0.2s ease",
      flexShrink: 0,
      whiteSpace: "nowrap",
      background:
        variant === "danger"
          ? dark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)"
          : variant === "primary"
          ? dark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.12)"
          : dark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)",
      color:
        variant === "danger"
          ? dark ? "#fca5a5" : "#b91c1c"
          : variant === "primary"
          ? dark ? "#bfdbfe" : BRAND
          : dark ? "#f1f5f9" : "#334155",
    }),

    // ── table ──
    tableWrap: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      border: `1px solid ${c.border}`,
      borderRadius: "14px",
    },
    table: (minWidth = 820) => ({ width: "100%", minWidth: `${minWidth}px`, borderCollapse: "collapse", fontSize: "14px" }),
    th: {
      textAlign: "start",
      padding: "14px 18px",
      fontSize: "12px",
      fontWeight: 700,
      letterSpacing: isRTL ? 0 : "0.04em",
      textTransform: isRTL ? "none" : "uppercase",
      color: c.muted,
      background: c.softBg,
      borderBottom: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
    },
    td: {
      padding: "16px 18px",
      borderBottom: `1px solid ${c.rowBorder}`,
      verticalAlign: "top",
      color: c.body,
      textAlign: "start",
    },
    cellTitle: { fontWeight: 700, color: c.text, overflowWrap: "anywhere", lineHeight: 1.45 },
    cellMeta: { fontSize: "12.5px", color: c.muted, marginTop: "3px", lineHeight: 1.5 },
    actionsCell: { display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" },

    badge: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "12px",
      fontWeight: 700,
      padding: "4px 10px",
      borderRadius: "999px",
      color,
      background: `${color}1f`,
      whiteSpace: "nowrap",
    }),
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
      background: c.chipBg,
      color: c.chipText,
      whiteSpace: "nowrap",
    },
    countPill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "22px",
      height: "22px",
      padding: "0 7px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 700,
      background: c.chipBg,
      color: c.chipText,
    },

    // Stats bar inside the history table
    bar: { height: "6px", borderRadius: "99px", background: c.track, overflow: "hidden" },
    barFill: (pct, color) => ({ width: `${pct}%`, height: "100%", background: color, borderRadius: "99px" }),

    // Info / warning boxes
    notice: (tone = "info") => ({
      display: "flex",
      gap: "10px",
      alignItems: "center",
      padding: "12px 14px",
      borderRadius: "12px",
      fontSize: "13.5px",
      lineHeight: 1.6,
      background:
        tone === "warning" ? (dark ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.08)") : c.chipBg,
      color: tone === "warning" ? (dark ? "#fbbf24" : "#b45309") : c.chipText,
      border: `1px solid ${tone === "warning" ? "rgba(245, 158, 11, 0.3)" : "rgba(46, 124, 186, 0.25)"}`,
    }),

    // Rule builder
    ruleRow: (exclude) => ({
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr auto" : isTablet ? "120px minmax(0, 1fr) auto" : "130px 180px minmax(0, 1fr) auto",
      gap: "8px",
      alignItems: "start",
      marginBottom: "8px",
      padding: "10px",
      borderRadius: "12px",
      background: exclude ? c.dangerSoft : c.softBg,
      border: `1px solid ${exclude ? "rgba(239, 68, 68, 0.3)" : c.border}`,
    }),

    // Search results list (user picker) — same palette as ReusableDropdown's menu
    menu: {
      position: "absolute",
      zIndex: 30,
      top: "calc(100% + 6px)",
      insetInline: 0,
      background: dark ? "#0f172a" : "#ffffff",
      border: `1px solid ${dark ? "rgba(148, 163, 184, 0.24)" : "#e2e8f0"}`,
      borderRadius: "12px",
      boxShadow: dark ? "0 16px 40px rgba(0, 0, 0, 0.45)" : "0 12px 32px rgba(15, 23, 42, 0.12)",
      maxHeight: "280px",
      overflowY: "auto",
    },
    menuItem: {
      display: "block",
      width: "100%",
      textAlign: "start",
      padding: "10px 14px",
      border: "none",
      borderBottom: `1px solid ${c.rowBorder}`,
      background: "transparent",
      color: c.text,
      cursor: "pointer",
      fontFamily: "inherit",
    },

    // Reach counter
    reachBox: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      padding: "14px 16px",
      borderRadius: "12px",
      marginTop: "14px",
      background: c.chipBg,
      border: "1px solid rgba(46, 124, 186, 0.25)",
    },

    // Preview
    previewLabel: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      fontSize: "12px",
      fontWeight: 700,
      color: c.muted,
      margin: "0 0 8px",
      textTransform: isRTL ? "none" : "uppercase",
      letterSpacing: isRTL ? 0 : "0.04em",
    },
    previewStage: (variant) => ({
      borderRadius: "14px",
      padding: variant === "modal" ? "18px" : "14px",
      minHeight: variant === "modal" ? "150px" : "auto",
      display: "flex",
      alignItems: variant === "modal" ? "center" : "flex-start",
      background: variant === "modal" ? "rgba(15, 23, 42, 0.45)" : c.softBg,
      border: `1px solid ${c.border}`,
    }),
    previewMeta: { display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "16px" },

    // Template cards
    cardGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))",
      gap: "16px",
    },
    templateCard: {
      border: `1px solid ${c.border}`,
      borderRadius: "16px",
      padding: "18px",
      background: dark ? "rgba(255, 255, 255, 0.02)" : "#ffffff",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      minWidth: 0,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    },
    templateIcon: (color) => ({
      width: "42px",
      height: "42px",
      borderRadius: "12px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: color.bg,
      color: color.accent,
      border: `1px solid ${color.border}`,
    }),
    templatePreview: {
      padding: "12px 14px",
      borderRadius: "12px",
      background: c.softBg,
      border: `1px solid ${c.rowBorder}`,
      fontSize: "13.5px",
      lineHeight: 1.6,
    },
    miniSwitch: (on) => ({
      position: "relative",
      width: "36px",
      height: "20px",
      borderRadius: "999px",
      flexShrink: 0,
      background: on ? BRAND : dark ? "rgba(255,255,255,0.18)" : "#cbd5e1",
      transition: "background 0.15s",
    }),
    miniSwitchKnob: (on) => ({
      position: "absolute",
      top: "2px",
      insetInlineStart: on ? "18px" : "2px",
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      background: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
      transition: "inset-inline-start 0.15s",
    }),

    emptyState: {
      textAlign: "center",
      padding: isMobile ? "36px 16px" : "48px 20px",
      color: c.muted,
    },
    emptyIcon: { opacity: 0.35, marginBottom: "12px" },
    emptyTitle: { fontSize: "15px", fontWeight: 600, color: c.body, margin: 0, lineHeight: 1.6 },

    // ── modals (same frame as Departments' ModalShell) ──
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.6)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: isMobile ? "16px" : "20px",
      direction: isRTL ? "rtl" : "ltr",
      animation: "bcFadeIn 0.2s ease",
    },
    // Same frame as the Add Employee modal: navy gradient, 16px radius.
    modal: (maxWidth = 520) => ({
      background: dark ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)" : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      color: dark ? "#e6eef8" : "#0f172a",
      borderRadius: "16px",
      width: "100%",
      maxWidth: isMobile ? "100%" : `${maxWidth}px`,
      maxHeight: "88vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      border: dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(148,163,184,0.15)",
      boxShadow: dark
        ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
        : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
      animation: "bcSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
    }),
    modalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      padding: isMobile ? "20px 20px 14px" : "28px 32px 16px",
      margin: isMobile ? "0 0 4px" : "0 0 8px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
    },
    modalTitleIcon: {
      display: "inline-flex",
      flexShrink: 0,
      color: dark ? "#60a5fa" : BRAND,
    },
    modalTitle: {
      fontSize: isMobile ? "19px" : "22px",
      fontWeight: 800,
      margin: 0,
      lineHeight: 1.4,
      minWidth: 0,
      overflowWrap: "anywhere",
      letterSpacing: isRTL ? 0 : "-0.5px",
      background: dark ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)" : "linear-gradient(135deg, #0f172a 0%, #2e7cba 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
    modalBody: { padding: isMobile ? "16px 20px" : "16px 32px 24px", overflowY: "auto", flex: 1 },
    modalFooter: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      padding: isMobile ? "16px 20px" : "20px 32px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
    },
  };
};

export const getBroadcastGlobalCss = (theme) => `
  .bc-row:hover td { background: ${theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(46,124,186,0.03)"}; }
  .bc-row:last-child td { border-bottom: none; }
  .bc-icon-btn:hover:not(:disabled) { background: ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"}; color: ${theme === "dark" ? "#ffffff" : "#0f172a"}; }
  .bc-icon-btn.danger:hover:not(:disabled) { background: rgba(239,68,68,0.12); color: #ef4444; }
  .bc-btn-primary:hover:not(:disabled) { background: ${BRAND_DARK} !important; }
  .bc-action-btn:hover { transform: translateY(-2px); filter: brightness(${theme === "dark" ? 1.15 : 0.96}); }
  .bc-tab:hover { color: ${theme === "dark" ? "#93c5fd" : BRAND} !important; }
  .bc-chip:hover { border-color: rgba(46,124,186,0.55) !important; }
  .bc-template-card:hover { border-color: rgba(46,124,186,0.45) !important; box-shadow: 0 8px 24px rgba(15,23,42,${theme === "dark" ? "0.35" : "0.08"}); }
  .bc-menu-item:hover { background: ${theme === "dark" ? "rgba(148,163,184,0.12)" : "#f1f5f9"} !important; }
  .bc-input:focus { border-color: rgba(46,124,186,0.6) !important; box-shadow: 0 0 0 3px rgba(46,124,186,0.12); }
  .bc-input::placeholder { color: ${theme === "dark" ? "rgba(203,213,225,0.5)" : "rgba(100,116,139,0.6)"}; }
  .bc-input::-webkit-calendar-picker-indicator { filter: ${theme === "dark" ? "invert(1)" : "none"}; opacity: 0.7; cursor: pointer; }
  .bc-scope *:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 2px; }
  .bc-check { width: 16px; height: 16px; margin: 0; accent-color: ${BRAND}; cursor: pointer; flex-shrink: 0; }
  .bc-modal-body { scrollbar-width: none; -ms-overflow-style: none; }
  .bc-modal-body::-webkit-scrollbar { display: none; }
  .bc-input:hover:not(:focus):not(:disabled) { border-color: ${theme === "dark" ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.16)"} !important; }
  @keyframes bcFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes bcSlideDown { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes bcSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes bcSpin { to { transform: rotate(360deg); } }
  @media (max-width: 1100px) {
    .bc-compose-grid { grid-template-columns: 1fr !important; }
    .bc-compose-preview { position: static !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    .bc-scope * { animation: none !important; transition: none !important; }
  }
`;

export default getBroadcastStyles;
